import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { DateRange } from "react-day-picker";
import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { InputForm } from "src/components/common/form/input/InputForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { SearchDateRangeForm } from "src/components/common/form/search/SearchDateRangeForm";
import { formatDate } from "src/utils/string/stringUtil";

import ModalWrapper from "../ModalWrapper";
import { SearchForm } from "../SearchForm";
import { SearchTable } from "./SearchTable";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { useCodeSearch } from "src/hooks/useCodeSearch";

import { SearchableDto, CodeType } from "src/types/base";
import { getCodeTypeFromTitle, getSearchFieldsByCodeType } from "src/utils/categoryUtils";
import { getCategoryByType } from "src/configs/categories";
import {
	CodeSearchModalProps,
	SearchState,
	SearchAction,
	FocusMoveConfig,
	SelectionEvent,
} from "src/types/code-search";

// ===== 검색 상태 관리 =====
const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
	switch (action.type) {
		case 'SET_FILTERS':
			return { ...state, filters: action.payload };
		case 'SET_GRID_DATA':
			return { ...state, gridData: action.payload };
		case 'SET_FILTERED_LIST':
			return { ...state, filteredList: action.payload };
		case 'SET_SELECTED':
			return { ...state, selected: action.payload };
		case 'SET_LOADING':
			return { ...state, isLoading: action.payload };
		case 'SET_ERROR':
			return { ...state, errorMessage: action.payload };
		case 'INITIALIZE':
			return { ...state, filters: action.payload.filters };
		case 'RESET':
			return {
				filters: {},
				gridData: [],
				filteredList: [],
				selected: undefined,
				isLoading: false,
				errorMessage: undefined
			};
		default:
			return state;
	}
};

// ===== 검색 상태 초기화 =====
const initialState: SearchState = {
	filters: {},
	gridData: [],
	filteredList: [],
	selected: undefined,
	isLoading: false,
	errorMessage: undefined
};

// ===== 커스텀 훅: 검색 로직 =====
const useSearchLogic = (codeSearchModalProps: CodeSearchModalProps | null) => {
	//state < 모달 상태
	//dispatch < 모달 상태 변경
	const [state, dispatch] = useReducer(searchReducer, initialState);
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	//searchForModalByType < 모달용 검색 API 호출 함수
	const { searchForModalByType } = useCodeSearch();

	//currentCodeType < 현재 카테고리의 CodeType 가져오기
	const currentCodeType = useMemo((): CodeType | null => {
		if (!codeSearchModalProps?.category) return null;
		return getCodeTypeFromTitle(codeSearchModalProps.category) || null;
	}, [codeSearchModalProps?.category]);

	// 검색 실행 함수 (디바운싱 적용)
	const executeSearch = useCallback(async () => {
		if (!currentCodeType) return; // 카테고리가 없으면 검색 실행 안함
		try {
			dispatch({ type: 'SET_LOADING', payload: true }); // 로딩 상태 설정
			dispatch({ type: 'SET_ERROR', payload: '' });
			setLoadingSpinner(true); 

			const responseData = await searchForModalByType(
				currentCodeType,
				state.filters['search'] || '',
				state.filters
			);

			const dataArray = Array.isArray(responseData) ? responseData : [];
			dispatch({ type: 'SET_GRID_DATA', payload: dataArray });
			dispatch({ type: 'SET_FILTERED_LIST', payload: dataArray });
		} catch (error) {
			console.error("Failed to fetch data", error);
			const errorMessage = error instanceof Error ? error.message : '데이터를 불러오는데 실패했습니다.';
			dispatch({ type: 'SET_ERROR', payload: errorMessage });
		} finally {
			dispatch({ type: 'SET_LOADING', payload: false });
			setLoadingSpinner(false);
		}
	}, [currentCodeType, state.filters, searchForModalByType, setLoadingSpinner]);

	// 클라이언트 필터링 — 현재 카테고리의 searchFields 기준으로 처리
	const applyClientFiltering = useCallback(() => {
		if (!currentCodeType) return;

		const searchTerm = state.filters['search'] || '';
		if (!searchTerm.trim()) {
			dispatch({ type: 'SET_FILTERED_LIST', payload: state.gridData });
			return;
		}

		const fields = getSearchFieldsByCodeType(currentCodeType);
		const searchLower = searchTerm.toLowerCase();
		const filtered = state.gridData.filter((item: any) =>
			fields.some((field) => item[field]?.toString().toLowerCase().includes(searchLower))
		);
		dispatch({ type: 'SET_FILTERED_LIST', payload: filtered });
	}, [state.gridData, state.filters, currentCodeType]);

	// 초기화 함수 (filters + dateRange UI 상태를 함께 초기화)
	const initializeSearch = useCallback((
		setDateRangesCallback: (ranges: Record<string, DateRange | undefined>) => void,
		clearExtraFilter: boolean = false
	) => {
		if (!codeSearchModalProps) return;

		const codeType = getCodeTypeFromTitle(codeSearchModalProps.category) || null;
		const category = codeType ? getCategoryByType(codeType) : undefined;
		const extraFilter = codeSearchModalProps.extraFilter || {};
		const initialDateRanges: Record<string, DateRange | undefined> = {};

		const defaultFilters: Record<string, string> = {
			search: clearExtraFilter ? '' : (codeSearchModalProps.search || '')
		};
		category?.searchOptions?.forEach(option => {
			if (option.type === 'dropdown' && option.defaultValue !== undefined) {
				defaultFilters[option.key] = option.defaultValue;
			}
			if (option.type === 'dateRange' && option.defaultDateRange === 'currentMonth') {
				const now = new Date();
				const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
				initialDateRanges[option.key] = { from: firstDay, to: now };
				defaultFilters[`${option.key}From`] = formatDate(firstDay);
				defaultFilters[`${option.key}To`] = formatDate(now);
			}
		});

		const processedExtraFilter = Object.fromEntries(
			Object.entries(extraFilter).map(([key, value]) => [key, String(value || 'all')])
		);

		setDateRangesCallback(initialDateRanges);
		dispatch({
			type: 'INITIALIZE',
			payload: {
				filters: {
					...defaultFilters,
					...(clearExtraFilter ? {} : processedExtraFilter),
				}
			}
		});
	}, [codeSearchModalProps]);

	return { state, dispatch, currentCodeType, executeSearch, applyClientFiltering, initializeSearch };
};

// ===== 커스텀 훅: 모달 생명주기 =====
const useModalLifecycle = (codeSearchModalProps: CodeSearchModalProps | null, codeSearchModalVisible: boolean) => {
	// 키보드 이벤트 관리
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Enter") {} };
		if (codeSearchModalVisible) window.addEventListener("keydown", handleKeyDown);
		return () => { window.removeEventListener("keydown", handleKeyDown); };
	}, [codeSearchModalVisible]);

	// 모달 초기화
	useEffect(() => {
		if (codeSearchModalProps?.category) {}
	}, [codeSearchModalProps?.category]);
};

export const CodeSearchModal = () => {
	const { codeSearchModalProps, codeSearchModalVisible, setCodeSearchModalVisible } = useUiuxStore();

	const commonCodeStore = useCommonCodeStore();

	const { state, dispatch, currentCodeType, executeSearch, applyClientFiltering, initializeSearch }
		= useSearchLogic(codeSearchModalProps || null);

	// dateRange는 DateRangePicker 상태를 유지해야 하므로 별도 상태로 관리
	const [dateRanges, setDateRanges] = useState<Record<string, DateRange | undefined>>({});
	const [pickerVisible, setPickerVisible] = useState(false);
	const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

	// DateRange 객체는 컴포넌트 상태에, API용 문자열은 filters에 반영
	const handleDateRangeChange = useCallback((key: string, range: DateRange | undefined) => {
		setDateRanges((prev) => ({ ...prev, [key]: range }));
		dispatch({
			type: 'SET_FILTERS',
			payload: {
				...state.filters,
				[`${key}From`]: range?.from ? formatDate(range.from) : '',
				[`${key}To`]: range?.to ? formatDate(range.to) : '',
			}
		});
	}, [dispatch, state.filters]);

	// 모달 크기 설정
	const categoryModalSize = useMemo(() => {
		if (!currentCodeType) return undefined;
		return getCategoryByType(currentCodeType)?.modalSize;
	}, [currentCodeType]);

	// 모달 생명주기 관리 훅 사용
	useModalLifecycle(codeSearchModalProps || null, codeSearchModalVisible);
	const moveFocusToNextCell = useCallback((config: FocusMoveConfig = {}) => {
		const { nextColumnKey, delay = 0 } = config;
		if (!nextColumnKey || !codeSearchModalProps?.gridRef?.current?.api) return;

		setTimeout(() => {
			const api = codeSearchModalProps.gridRef?.current?.api;
			if (!api) return;

			// 현재 선택된 행의 인덱스 찾기
			let targetRowIndex = -1;
			api.forEachNode((node: any) => {
				if (node.data?.valueIndex === codeSearchModalProps?.rowIndex && node.rowIndex !== null) {
					targetRowIndex = node.rowIndex;
				}
			});

			if (targetRowIndex !== -1) {
				api.startEditingCell({ rowIndex: targetRowIndex, colKey: nextColumnKey });
			}
		}, delay);
	}, [codeSearchModalProps]);

	// 선택 이벤트 처리 함수 추가
	const handleSelection = useCallback((selectedItem: SearchableDto, event?: SelectionEvent) => {
		if (!codeSearchModalProps?.onConfirm) return;

		const shouldMoveFocus = !event || (event.type === 'keyboard' || event.detail === 2);
		codeSearchModalProps.onConfirm(selectedItem, event);

		// 포커스 이동이 필요한 경우
		if (shouldMoveFocus && codeSearchModalProps.focusMoveConfig) {
			moveFocusToNextCell(codeSearchModalProps.focusMoveConfig);
		}
	}, [codeSearchModalProps, moveFocusToNextCell]);

	// 1. 검색 조건 변경 시 API 호출 (디바운싱 적용)
	useEffect(() => {
		if (!currentCodeType) return;
		const timeoutId = setTimeout(() => { executeSearch(); }, 300);
		return () => clearTimeout(timeoutId);
	}, [currentCodeType, state.filters, executeSearch]);

	// 2. 그리드 데이터 변경 시 클라이언트 필터링
	useEffect(() => {
		applyClientFiltering();
	}, [state.gridData, state.filters, applyClientFiltering]);

	// 3. 모달 props 변경 시 초기화 (카테고리가 변경될 때만 실행)
	useEffect(() => {
		if (codeSearchModalProps) {
			setPickerVisible(false);
			initializeSearch(setDateRanges);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [codeSearchModalProps?.category]);

	/** 검색 조건(filters) 변경 — search, dropdown, dateRange 등 state.filters 갱신 후 useEffect에서 API 재조회 */
	const handleFilterChange = useCallback((key: string, value: string) => {
		dispatch({ type: 'SET_FILTERS', payload: { ...state.filters, [key]: value } });
	}, [dispatch, state.filters]);

	/** SearchTable 행 선택 — 확인 버튼 클릭 시 onConfirm에 넘길 항목을 state.selected에 저장 */
	const handleItemSelect = useCallback((item: SearchableDto | undefined) => {
		dispatch({ type: 'SET_SELECTED', payload: item });
	}, [dispatch]);

	/**
	 * 하단 [확인] 버튼 클릭 처리
	 * 1. state.selected(그리드에서 고른 행) 검증
	 * 2. handleSelection → 호출 측 onConfirm(그리드/폼 행 반영) + focusMoveConfig 시 다음 셀 편집
	 * 3. 모달 닫기 후 onCloseConfirm(선택적 후처리) 호출
	 */
	const handleConfirmClick = useCallback(() => {
		if (!codeSearchModalProps) return;
		if (!state.selected) {
			alert('선택된 항목이 없습니다.');
			return;
		}
		// 선택 처리 및 포커스 이동 (버튼 클릭도 포커스 이동 대상)
		handleSelection(state.selected);
		setCodeSearchModalVisible(false);
		if (codeSearchModalProps.onCloseConfirm) codeSearchModalProps.onCloseConfirm(state.selected);
	}, [state.selected, codeSearchModalProps, setCodeSearchModalVisible, handleSelection]);

	const renderSearchOptions = () => {
		if (!currentCodeType) return null;

		const searchOptions = getCategoryByType(currentCodeType)?.searchOptions;
		if (!searchOptions || searchOptions.length === 0) return null;

		return (
			<div className="w-full h-[10%] flex items-center px-[8px]">
				<div className="w-full flex items-center gap-[8px]">

					{searchOptions.map(option => {
						if (option.type === 'input') {
							return (
								<InputForm
									key={option.key}
									title={option.label}
									value={state.filters[option.key] || ''}
									editable={true}
									placeholder={option.placeholder}
									wrapperOption={{
										wrapperStyle: "flex-1 mb-[5px]",
										titleStyle: "w-[60px] min-w-[60px]",
										inputStyle: "flex-1"
									}}
									onChange={(e) => handleFilterChange(option.key, e.target.value)}
									onKeyDown={(e) => e.key === 'Enter' && executeSearch()}
								/>
							);
						}
						if (option.type === 'dropdown') {
							const data = option.dataKey ? (commonCodeStore as any)[option.dataKey] ?? [] : [];
							return (
								<DropDownForm
									key={option.key}
									title={option.label}
									name={option.key}
									value={state.filters[option.key] ?? option.defaultValue ?? 'all'}
									data={data}
									wrapperOption={{
										wrapperStyle: "mb-[5px]",
										titleStyle: "w-[60px] min-w-[60px]",
										inputStyle: "w-[120px]"
									}}
									onChange={(e) => handleFilterChange(option.key, e.target.value)}
								/>
							);
						}
						if (option.type === 'dateRange') {
							return (
								<SearchDateRangeForm
									key={option.key}
									label={option.label}
									visible={pickerVisible}
									setVisible={setPickerVisible}
									dateRange={dateRanges[option.key]}
									setDateRange={(range) => handleDateRangeChange(option.key, range)}
									pickerPosition={pickerPosition}
									setPickerPosition={setPickerPosition}
									wrapperOption={{
										wrapperStyle: "mb-[5px]",
										titleStyle: "form-title w-[60px] min-w-[60px] mr-[4px]",
										dateRangeStyle: "border border-gray-300 py-[1px] text-center w-full h-[24px] rounded-[4px] px-[4px] bg-white whitespace-nowrap overflow-hidden text-ellipsis ",
									}}
								/>
							);
						}
					})}

					<div className="flex items-center gap-2 ml-auto">
						<button
							onClick={() => {
								dispatch({ type: 'RESET' });
								setPickerVisible(false);
								initializeSearch(setDateRanges, true);
							}}
							className="h-[30px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold"
						>
							초기화
						</button>
						<button
							onClick={executeSearch}
							className="h-[30px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold"
						>
							조회
						</button>
					</div>
				</div>
			</div>
		);
	};

	/**
	 * categories.ts 기반 범용 검색 그리드 렌더링
	 * - API가 카테고리별 데이터를 내려주므로 타입가드 재필터링 없이 filteredList를 사용
	 * - SearchTable: codeType에 맞는 컬럼·더블클릭 확인은 categories 설정 사용
	 */
	const renderTable = () => {
		if (!currentCodeType) return <div>지원하지 않는 카테고리입니다.</div>;

		return (
			<SearchTable
				codeType={currentCodeType}
				formName={codeSearchModalProps?.formName || ''}
				data={state.filteredList}
				selected={state.selected}
				setSelected={handleItemSelect} // 클릭 시 state.selected 갱신 ([확인] 버튼용)
				onConfirm={(item: any) => handleSelection(item)} // 더블클릭·Enter 시 즉시 onConfirm + 모달은 SearchTable에서 닫음
				onCloseConfirm={codeSearchModalProps?.onCloseConfirm}
				processType_focus={codeSearchModalProps?.processType_focus || 'normal'}
			/>
		);
	};

	// 에러 메시지 렌더링
	const renderErrorMessage = () => {
		if (!state.errorMessage) return null;
		return (
			<div className="px-4 py-2 bg-red-50 text-red-600 text-sm border border-red-200 rounded">
				{state.errorMessage}
			</div>
		);
	};
	/** 검색 조건 유무 확인 (검색 조건이 없으면 검색어 입력 필드 미렌더링) */
	const hasSearchOptions = currentCodeType
		? (getCategoryByType(currentCodeType)?.searchOptions?.length ?? 0) > 0
		: false;

	return (
		<ModalWrapper
			size={categoryModalSize?.size || codeSearchModalProps?.wrapperOption?.size || 'md'}
			width={categoryModalSize?.width || codeSearchModalProps?.wrapperOption?.width || 1200}
			overlay={codeSearchModalProps?.wrapperOption?.overlay || true}
			onOverlayClick={() => setCodeSearchModalVisible(false)}
			onEscKeyDown={() => setCodeSearchModalVisible(false)}
		>
			<div className="w-full h-[10%] flex items-center px-[24px] bg-gray-100">
				<p className="font-bold">
					코드 검색 ({codeSearchModalProps?.category} 정보)
				</p>
			</div>

			{renderSearchOptions()}

			{!hasSearchOptions && (
				<div className="w-full h-[10%] flex items-center px-[8px]">
					<SearchForm
						value={state.filters['search'] || ''}
						setSearch={(value) => handleFilterChange('search', value)}
						onSearch={() => {}}
					/>
				</div>
			)}

			{renderErrorMessage()}

			<div className="w-full h-[70%] px-[8px]">
				{renderTable()}
			</div>

			<div className="w-full h-[10%] flex justify-end items-center gap-[8px] px-[8px]">
				{codeSearchModalProps?.onClear && (
					<button
						className="text-white rounded-[4px] bg-blue-dark px-[16px] py-[6px] font-semibold"
						onClick={() => {
							codeSearchModalProps?.onClear?.();
							setCodeSearchModalVisible(false);
						}}
					>
						비우기
					</button>
				)}
				<button
					className="text-white rounded-[4px] bg-blue-dark px-[16px] py-[6px] font-semibold"
					onClick={handleConfirmClick}
				>
					확인
				</button>
				<button
					className="text-white rounded-[4px] bg-blue-dark px-[16px] py-[6px] font-semibold"
					onClick={() => {
						codeSearchModalProps?.onClear?.();
						setCodeSearchModalVisible(false);
					}}
				>
					취소
				</button>
			</div>
		</ModalWrapper>
	);
};
